import { errorMessage } from "../../utils/mutationResult.js";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneFiles, removePendingFilesByClientIds } from "./fileSlice.js";
import {
  fetchOrQueue,
  cancelOutboxItemsForEntity,
} from "../../lib/offlineQueue.js";
import {
  getCachedData,
  putCachedData,
  getAllOutboxItems,
  updateOutboxItem,
} from "../../lib/offlineDb.js";
import { upsertOutboxItem } from "./offlineSlice.js";
import { resolveEntityId, canonicalEntityId, isTemporaryEntityId, readLocalReportEntities, createReportReadGuard } from "../../lib/syncedEntityIds";

// Initial state
const initialState = {
  works: [],
  currentWork: null,
  workStats: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  filters: {
    projectId: "",
    assignedTo: "",
    status: "",
    workType: "",
    priority: "",
  },
};

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8005/api";

const getActiveReportId = () =>
  window.sessionStorage?.getItem("activeReportId") ||
  window.localStorage?.getItem("activeReportId") ||
  "";

// Async thunks for work items
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, Record<string, unknown> | void, {}>} */
export const fetchWorks = createAsyncThunk(
  "works/fetchWorks",
  async (params = {}, { rejectWithValue, getState }) => {
    const cacheKey = `works:${JSON.stringify(params)}`;
    try {
      const state = getState();
      const reportId = canonicalEntityId(state, "report", params.reportId || getActiveReportId());
      const siteId = canonicalEntityId(state, "site", params.siteId);
      params = { ...params, ...(siteId ? { siteId } : {}) };
      const localDuringSync = createReportReadGuard(getState, reportId);
      if (localDuringSync() || isTemporaryEntityId(reportId) || isTemporaryEntityId(siteId)) {
        const work = await readLocalReportEntities(state, "works", reportId, siteId, params.templateId);
        return { work, pagination: { currentPage: 1, totalPages: 1, totalItems: work.length, itemsPerPage: work.length } };
      }
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        const cached = await getCachedData(cacheKey);
        if (cached) return cached;
      }
      const queryParams = new URLSearchParams({
        ...params,
        ...(reportId ? { reportId } : {}),
      }).toString();
      const response = await fetch(`${API_BASE_URL}/works?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(errorMessage(error, "Failed to fetch work items"));
      }

      const data = await response.json();
      if (localDuringSync()) {
        const work = await readLocalReportEntities(getState(), "works", reportId, siteId, params.templateId);
        return { work, pagination: { currentPage: 1, totalPages: 1, totalItems: work.length, itemsPerPage: work.length } };
      }
      await putCachedData(cacheKey, data.data);
      return data.data;
    } catch (error) {
      const cached = await getCachedData(cacheKey).catch(() => null);
      if (cached) return cached;
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const fetchWorkById = createAsyncThunk(
  "works/fetchWorkById",
  async (workId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/works/${workId}?populate=true`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(errorMessage(error, "Failed to fetch work item"));
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const createWork = createAsyncThunk(
  "works/createWork",
  async (workData, { rejectWithValue, dispatch }) => {
    try {
      const reportId = workData?.reportId || getActiveReportId();
      const clientId = `offline-work-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const {
        _pendingImportedFiles = [],
        _importedLocalId,
        _sourceId,
        _id,
        id,
        _clientId: _payloadClientId,
        _offlineQueued,
        ...workPayload
      } = workData || {};
      const requestBody = JSON.stringify({
        ...workPayload,
        ...(reportId ? { reportId } : {}),
      });


      const outcome = await fetchOrQueue({
        dispatch,
        requestInit: {
          url: `${API_BASE_URL}/works`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: requestBody,
        },
        meta: {
          description: `Create work item ${clientId}`,
          entityType: "work",
          entityId: clientId,
          clientId,
          requestType: "work-create",
        },
        performRequest: async () => {
          const response = await fetch(`${API_BASE_URL}/works`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            credentials: "include",
            body: requestBody,
          });
          if (!response.ok) {
            const error = await response.json();
            const err = new Error(
              errorMessage(error, "Failed to create work item"),
            );
            err.status = response.status;
            throw err;
          }
          return response.json();
        },
      });

      if (outcome.queued) {
        return {
          ...workPayload,
          ...(reportId ? { reportId } : {}),
          _id: clientId,
          id: clientId,
          _clientId: clientId,
          _offlineQueued: true,
        };
      }
      const data = outcome.result;
      if (_pendingImportedFiles.length > 0 && data?.data?._id) {
        await dispatch(
          cloneFiles({
            files: _pendingImportedFiles.map((file) => ({
              ...file,
              relatedTo: {
                ...(file.relatedTo || {}),
                entityId: data.data._id || file?.relatedTo?.entityId,
              },
            })),
            entityType: workPayload?.entityType || "work",
            // siteId: workPayload.siteId,
            // reportId,
            entityId: data.data._id,
            // entityType: "work",
          }),
        ).unwrap();
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const updateWork = createAsyncThunk(
  "works/updateWork",
  async ({ workId, workData }, { rejectWithValue, dispatch, getState }) => {
    try {
      workId = await resolveEntityId(getState(), "work", workId);
      const reportId = workData?.reportId || getActiveReportId();
      const {
        _pendingImportedFiles = [],
        _importedLocalId,
        _sourceId,
        // _id,
        // id,
        // _clientId: _payloadClientId,
        // _offlineQueued,
        ...workPayload
      } = workData || {};
      // const clientId = _payloadClientId || workId;
      const requestUrl = `${API_BASE_URL}/works/${workId}`;
      const requestHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      const requestBody = JSON.stringify({
        ...workPayload,
        ...(reportId ? { reportId } : {}),
      });

      // If the work was created offline, update its queued POST instead of
      // queueing a PUT to the temporary id. This preserves one create record
      // and lets subsequent file uploads resolve the eventual server id.
      if (String(workId).startsWith("offline-work-")) {
        const queuedItems = await getAllOutboxItems();
        const createItem = queuedItems.find(
          (item) =>
            // item.meta?.requestType === "work-create" &&
            item.type === "work-create" &&
            item.status !== "syncing" &&
            (item.meta?.clientId === workId || item.meta?.entityId === workId),
        );
        if (createItem) {
          const currentBody = JSON.parse(createItem.body || "{}");
          const updatedItem = await updateOutboxItem(createItem.id, {
            status: "pending", retryable: true, retryCount: 0, lastError: null, errorKind: null,
            body: JSON.stringify({
              ...currentBody,
              ...workPayload,
              ...(reportId ? { reportId } : {}),
            }),
          });
          if (updatedItem) dispatch(upsertOutboxItem(updatedItem));
          return {
            ...workPayload,
            _id: workId,
            _clientId: workId,
            reportId,
            _offlineQueued: true,
          };
          // if (updatedItem) dispatch({ type: "offline/upsertOutboxItem", payload: updatedItem });
          // const existing = getState()?.works?.works?.find((w) => w._id === workId) || getState()?.works?.currentWork;
          // console.log("existing and workPayload for update work:", existing, workPayload);
          // return { ...existing, ...workPayload, _id: workId, _clientId: workId, reportId, _offlineQueued: true };
        }
      }

      // Phase 2: if the device is offline, queue this save in the outbox
      // instead of failing — the auditor's edits are kept and applied
      // optimistically, and the request is replayed once reconnected.
      const outcome = await fetchOrQueue({
        dispatch,
        requestInit: {
          url: requestUrl,
          method: "PUT",
          headers: requestHeaders,
          body: requestBody,
        },
        meta: {
          description: `Update work item ${workId}`,
          entityType: "work",
          entityId: workId,
          clientId: workId,
          requestType: "work-update",
        },
        performRequest: async () => {
          const response = await fetch(requestUrl, {
            method: "PUT",
            headers: requestHeaders,
            credentials: "include",
            body: requestBody,
          });
          if (!response.ok) {
            const error = await response.json();
            const err = new Error(
              errorMessage(error, "Failed to update work item"),
            );
            err.status = response.status;
            throw err;
          }
          return response.json();
        },
      });

      if (outcome.queued) {
        const existing =
          getState()?.works?.works?.find(
            (w) => w._id === workId || w._clientId === workId,
          ) || getState()?.works?.currentWork;
        return {
          ...existing,
          ...workPayload,
          _id:existing?._id || workId,
          _clientId: existing?._clientId || workId,
          _offlineQueued: true,
        };
      }

      const data = outcome.result;
      return data?.data?.observation || data?.data || data;
      // return {
      //   ...data.data,
      //   _clientId: data.data?._clientId || workId,
      // };
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },

);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const deleteWork = createAsyncThunk(
  "works/deleteWork",
  async (workId, { rejectWithValue, dispatch, getState }) => {
    try {
      const existing = getState()?.works?.works?.find(
        (w) => w._id === workId || w._clientId === workId,
      );
      const entityId = existing?._id || workId;
      const clientId = existing?._clientId || entityId;
      const state = getState();
      const fileIds = [
        ...(state?.files?.files || []),
        ...(state?.auditReports?.currentReportData?.files || []),
        ...Object.values(state?.auditReports?.allReportsData || {}).flatMap((report) => report?.files || []),
      ]
        .filter((file) => file?.relatedTo?.entityType === "work" &&
          [entityId, clientId].includes(file.relatedTo.entityId))
        .flatMap((file) => [file._id, file.id, file._clientId])
        .filter(Boolean);

      // A work item created entirely offline never reached the server (its
      // id is still the local "offline-work-..." clientId), so deleting it
      // means cancelling the queued create — and any queued edits or image
      // uploads made to it before the delete — instead of sending a delete
      // request the server has no matching record for.
      const neverSynced = String(entityId).startsWith("offline-work-");

      if (neverSynced) {
        const cancelled = await cancelOutboxItemsForEntity(dispatch, {
          entityType: "work",
          entityId,
          clientId,
          fileIds,
        });

        const cancelledFileClientIds = cancelled
          .filter((item) => item.type === "file-upload")
          .map((item) => item.clientId)
          .filter(Boolean);

        if (cancelledFileClientIds.length > 0) {
          dispatch(removePendingFilesByClientIds(cancelledFileClientIds));
        }
        return workId;
      }

      // Deleting supersedes any not-yet-synced edit still queued for this
      // work item — cancel it so it doesn't try to PATCH/PUT an entity
      // that's about to be gone.
      const cancelled = await cancelOutboxItemsForEntity(dispatch, {
        entityType: "work",
        entityId,
        clientId,
        fileIds,
      });
      const cancelledFileClientIds = cancelled
        .filter((item) => item.type === "file-upload")
        .map((item) => item.clientId)
        .filter(Boolean);
      if (cancelledFileClientIds.length > 0) {
        dispatch(removePendingFilesByClientIds(cancelledFileClientIds));
      }

      await fetchOrQueue({
        dispatch,
        requestInit: {
          url: `${API_BASE_URL}/works/${workId}`,
          method: "DELETE",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
        meta: {
          description: `Delete work item ${workId}`,
          entityType: "work",
          entityId: workId,
          requestType: "work-delete",
        },
        performRequest: async () => {
          const response = await fetch(`${API_BASE_URL}/works/${workId}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            credentials: "include",
          });
          if (!response.ok) {
            const error = await response.json();
            const err = new Error(
              errorMessage(error, "Failed to delete work item"),
            );
            err.status = response.status;
            throw err;
          }
          return response;
        },
      });

      return workId;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const startWork = createAsyncThunk(
  "works/startWork",
  async (workId, { rejectWithValue, dispatch, getState }) => {
    try {
      const requestUrl = `${API_BASE_URL}/works/${workId}/start`;
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      const outcome = await fetchOrQueue({
        dispatch,
        requestInit: { url: requestUrl, method: "PATCH", headers },
        meta: {
          description: `Start work item ${workId}`,
          entityType: "work",
          entityId: workId,
          requestType: "work-start",
        },
        performRequest: async () => {
          const response = await fetch(requestUrl, {
            method: "PATCH",
            headers,
            credentials: "include",
          });
          if (!response.ok) {
            const error = await response.json();
            const err = new Error(errorMessage(error, "Failed to start work item"));
            err.status = response.status;
            throw err;
          }
          return response.json();
        },
      });
      if (outcome.queued) {
        const existing = getState()?.works?.works?.find(
          (work) => work._id === workId,
        );
        return {
          ...existing,
          _id: workId,
          status: "in-progress",
          _offlineQueued: true,
        };
      }
      return outcome.result.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const completeWork = createAsyncThunk(
  "works/completeWork",
  async (
    { workId, completionData },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const requestUrl = `${API_BASE_URL}/works/${workId}/complete`;
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      const body = JSON.stringify(completionData);
      const outcome = await fetchOrQueue({
        dispatch,
        requestInit: { url: requestUrl, method: "PATCH", headers, body },
        meta: {
          description: `Complete work item ${workId}`,
          entityType: "work",
          entityId: workId,
          requestType: "work-complete",
        },
        performRequest: async () => {
          const response = await fetch(requestUrl, {
            method: "PATCH",
            headers,
            credentials: "include",
            body,
          });
          if (!response.ok) {
            const error = await response.json();
            const err = new Error(
              errorMessage(error, "Failed to complete work item"),
            );
            err.status = response.status;
            throw err;
          }
          return response.json();
        },
      });
      if (outcome.queued) {
        const existing = getState()?.works?.works?.find(
          (work) => work._id === workId,
        );
        return {
          ...existing,
          ...completionData,
          _id: workId,
          status: "completed",
          _offlineQueued: true,
        };
      }
      return outcome.result.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const fetchWorkStats = createAsyncThunk(
  "works/fetchWorkStats",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/works/stats/${projectId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(
          errorMessage(error, "Failed to fetch work statistics"),
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

// Work slice
const workSlice = createSlice({
  name: "works",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentWork: (state, action) => {
      state.currentWork = action.payload;
    },
    clearCurrentWork: (state) => {
      state.currentWork = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        projectId: "",
        assignedTo: "",
        status: "",
        workType: "",
        priority: "",
      };
    },
    updateWorkStatus: (state, action) => {
      const { workId, status } = action.payload;
      const work = state.works.find((w) => w._id === workId);
      if (work) {
        work.status = status;
      }
      if (state.currentWork && state.currentWork._id === workId) {
        state.currentWork.status = status;
      }
    },
    setWorksFromVersion: (state, action) => {
      // Used when loading historical version data
      state.works = action.payload;
    },
    // Called once a queued offline "work-create" successfully syncs, so the
    // optimistic local work item (matched by its offline clientId) is
    // reconciled with the real server record instead of leaving the local
    // "offline-work-..." id in place, which would break any later edit or
    // image upload made against it.
    addSyncedWork: (state, action) => {
      const { work, clientId } = action.payload || {};
      if (!work?._id) return;
      const syncedWork = clientId ? { ...work, _clientId: clientId } : work;
      const matchesClientWork = (item) =>
        Boolean(clientId) &&
        (item?._clientId === clientId ||
          item?._id === clientId ||
          item?.id === clientId);
      const index = state.works.findIndex(matchesClientWork);
      if (index === -1) state.works.unshift(syncedWork);
      else state.works[index] = { ...state.works[index], ...syncedWork };
      if (matchesClientWork(state.currentWork)) {
        state.currentWork = { ...state.currentWork, ...syncedWork };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch works cases
      .addCase(fetchWorks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorks.fulfilled, (state, action) => {
        state.loading = false;
        state.works = action.payload.work;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchWorks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch work by ID cases
      .addCase(fetchWorkById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWork = action.payload;
        state.error = null;
      })
      .addCase(fetchWorkById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create work cases
      .addCase(createWork.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWork.fulfilled, (state, action) => {
        state.loading = false;
        const item = action.payload;
        const existingIndex = state.works.findIndex(
          (work) =>
            work._clientId &&
            item._clientId &&
            work._clientId === item._clientId,
        );
        if (existingIndex >= 0)
          state.works[existingIndex] = {
            ...state.works[existingIndex],
            ...item,
          };
        else state.works.unshift(item);
        state.error = null;
      })
      .addCase(createWork.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update work cases
      .addCase(updateWork.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWork.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.works.findIndex(
          (w) =>
            w._id === action.payload._id ||
            w._clientId === action.payload._clientId,
        );
        if (index !== -1) {
          state.works[index] = { ...state.works[index], ...action.payload };
        }
        if (
          state.currentWork &&
          (state.currentWork._id === action.payload._id ||
            state.currentWork._clientId === action.payload._clientId)
        ) {
          state.currentWork = { ...state.currentWork, ...action.payload };
        }
        state.error = null;
      })
      .addCase(updateWork.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete work cases
      .addCase(deleteWork.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWork.fulfilled, (state, action) => {
        state.loading = false;
        state.works = state.works.filter((w) => w._id !== action.payload);
        if (state.currentWork && state.currentWork._id === action.payload) {
          state.currentWork = null;
        }
        state.error = null;
      })
      .addCase(deleteWork.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Start work cases
      .addCase(startWork.fulfilled, (state, action) => {
        const index = state.works.findIndex(
          (w) => w._id === action.payload._id,
        );
        if (index !== -1) {
          state.works[index] = action.payload;
        }
        if (state.currentWork && state.currentWork._id === action.payload._id) {
          state.currentWork = action.payload;
        }
      })

      // Complete work cases
      .addCase(completeWork.fulfilled, (state, action) => {
        const index = state.works.findIndex(
          (w) => w._id === action.payload._id,
        );
        if (index !== -1) {
          state.works[index] = action.payload;
        }
        if (state.currentWork && state.currentWork._id === action.payload._id) {
          state.currentWork = action.payload;
        }
      })

      // Fetch work stats cases
      .addCase(fetchWorkStats.fulfilled, (state, action) => {
        state.workStats = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentWork,
  clearCurrentWork,
  setFilters,
  clearFilters,
  updateWorkStatus,
  setWorksFromVersion,
  addSyncedWork,
} = workSlice.actions;

export default workSlice.reducer;
